import { useEffect, useState } from 'react'
import { useRouter, useSearch } from '@tanstack/react-router'
import type { SignInRefusal } from '@domain/ports/auth-gateway'
import { Glass } from '@ui/glass/Glass'
import { useAuth } from './auth-context'
import { safeDestination } from './destination'
import { Interstitial, Waiting } from './Interstitial'

/**
 * A mensagem de cada recusa.
 *
 * `not-allowed` tem texto próprio porque o Passo 2 da Task 8 exige: quem não
 * está na allowlist precisa ler que a conta não tem acesso, não "erro ao
 * entrar" — a segunda mensagem convida a tentar de novo para sempre.
 *
 * `cancelled` não vira mensagem nenhuma: fechar o popup é uma decisão da
 * pessoa, e o app não comenta o que ela decidiu.
 */
function signInMessage(refusal: SignInRefusal): string | null {
  switch (refusal) {
    case 'not-allowed':
      return 'Esta conta não tem acesso ao Porquinho.'
    case 'cancelled':
      return null
    case 'failed':
      return 'Não foi possível entrar agora. Tente de novo.'
  }
}

export function SignIn() {
  const { session, signIn } = useAuth()
  const router = useRouter()
  const { destino } = useSearch({ from: '/entrar' })
  const destination = safeDestination(destino)
  const [refusal, setRefusal] = useState<SignInRefusal | null>(null)
  const [busy, setBusy] = useState(false)

  const ready = session.status === 'ready'
  useEffect(() => {
    if (!ready) return
    // `history.replace` em vez de `navigate({ to })`: o destino é uma string
    // vinda da URL, e o `to` do TanStack é tipado contra a árvore de rotas —
    // uma string dinâmica só entraria ali com cast. O histórico aceita caminho,
    // e o roteador reage a ele. `safeDestination` é quem garante que a string é
    // um caminho interno.
    router.history.replace(destination)
  }, [ready, destination, router])

  async function handleSignIn() {
    setBusy(true)
    setRefusal(null)
    const result = await signIn()
    setBusy(false)
    if (!result.ok) setRefusal(result.refusal)
  }

  switch (session.status) {
    case 'loading':
    case 'preparing':
    case 'unprepared':
      return <Interstitial session={session} />
    case 'ready':
      // Já entrou: o efeito acima está levando ao destino. Nada de formulário
      // aqui — a tela de login para quem tem sessão é a piscada proibida.
      return <Waiting label="Entrando" quiet />
    case 'signedOut':
      break
  }

  const message = refusal === null ? null : signInMessage(refusal)

  return (
    <section className="mx-auto flex min-h-dvh max-w-md items-center justify-center px-4">
      <Glass level="frost" className="w-full p-6 text-center">
        <h1 className="titulo mb-1 text-2xl">Porquinho</h1>
        <p className="mb-6 text-sm text-[var(--text-muted)]">
          As finanças da casa, para as duas pessoas que moram nela.
        </p>
        <button
          type="button"
          onClick={() => void handleSignIn()}
          disabled={busy}
          className="w-full rounded-[var(--radius-md)] bg-[var(--brand)] px-4 py-3 text-sm font-semibold
                     text-white disabled:opacity-60"
        >
          {busy ? 'Entrando…' : 'Entrar com o Google'}
        </button>
        {message !== null && (
          <p role="alert" className="mt-4 text-sm text-[var(--text-muted)]">
            {message}
          </p>
        )}
      </Glass>
    </section>
  )
}
